// ============================================
// ReturnsPage — xử lý đổi/trả tại quầy (sheet 2 mục #55/#56/#59)
// ============================================
//
// Contract BE document 03/09/2026 (trước đó có trong code nhưng chưa document,
// nên store-admin không có màn này suốt thời gian dài).
//
// Ba điều dễ làm sai, đừng gỡ khi refactor:
//
// 1. `actionable` + `blocked_reason` của endpoint scan mới là thứ quyết định
//    UI, không phải data. Chưa duyệt mà nhận hàng = cửa hàng giữ hàng cho một
//    yêu cầu chưa ai đồng ý, đường lùi duy nhất là từ chối hoàn tiền cho hàng
//    đang cầm trong tay.
// 2. Nhận hàng có SỐ THỰC NHẬN từng dòng. Khách khai 3 mang đến 2 là chuyện
//    thường; `refund_amount` được BE tính lại theo số thực nhận — luôn hiện
//    số MỚI sau khi nhận, đừng hiện số lúc tạo yêu cầu.
// 3. Quyền tách 2 tầng: việc ở quầy (quét, xem, nhận hàng) cho OPERATOR trở
//    lên; quyết định về TIỀN (duyệt/từ chối/hoàn) chỉ STORE_ADMIN/AI_ADMIN.
//    Bắt quản lý có mặt mỗi lần khách mang hàng đến là sai vận hành.

import { useMemo, useState } from 'react';
import { isApiError } from '@conai/api-client';
import type { ReturnRequest, ReturnRequestItem, UUID } from '@conai/shared-types';
import { useI18n } from '../../i18n';
import {
  PageContainer,
  PageHeader,
  SelectDropdown,
  DataTable,
  Pagination,
  type Column,
} from '../../components/shared';
import {
  useStaffReturns,
  useStaffScanReturn,
  useApproveReturn,
  useRejectReturn,
  useReceiveReturn,
  useRefundReturn,
} from '../../hooks/useReturns';
import { useAuthStore, selectUserRole } from '../../stores/auth.store';
import { useStoreSelectionStore } from '../../stores';
import { extractList } from '../../utils/apiList';
import { formatMoney } from '../../utils/currency';

const PAGE_SIZE = 20;

const STATUS_STYLES: Record<string, string> = {
  PENDING_APPROVAL: 'bg-amber-50 text-amber-700',
  APPROVED: 'bg-blue-50 text-blue-700',
  ITEMS_RECEIVED: 'bg-indigo-50 text-indigo-700',
  REFUND_SENT: 'bg-purple-50 text-purple-700',
  REFUNDED: 'bg-emerald-50 text-emerald-700',
  REJECTED: 'bg-red-50 text-red-700',
  CANCELLED: 'bg-slate-100 text-slate-600',
  EXPIRED: 'bg-slate-100 text-slate-600',
};

function formatDateTime(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function ReturnsPage() {
  const { t } = useI18n();
  const role = useAuthStore(selectUserRole);
  const selectedStoreId = useStoreSelectionStore((s) => s.selectedStoreId);

  /** Quyết định về tiền — duyệt / từ chối / hoàn. */
  const canDecideMoney = role === 'STORE_ADMIN' || role === 'AI_ADMIN';

  const [view, setView] = useState<'queue' | 'awaiting'>('queue');
  const [currentPage, setCurrentPage] = useState(1);
  const [selected, setSelected] = useState<ReturnRequest | null>(null);

  // ---- Tra cứu ở quầy ----
  const [scanInput, setScanInput] = useState('');
  const { mutate: scanReturn, isPending: isScanning } = useStaffScanReturn();
  const [scanBlocked, setScanBlocked] = useState<string | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);

  const { data, isLoading, error } = useStaffReturns({
    page: currentPage,
    page_size: PAGE_SIZE,
    store_id: selectedStoreId || undefined,
    awaiting_approval: view === 'awaiting' ? true : undefined,
  });

  const rows = useMemo(() => extractList<ReturnRequest>(data), [data]);
  const totalItems = (data as { count?: number } | undefined)?.count ?? rows.length;
  const totalPages = Math.max(Math.ceil(totalItems / PAGE_SIZE), 1);

  const handleScan = () => {
    const value = scanInput.trim();
    if (!value || isScanning) return;
    setScanBlocked(null);
    setScanError(null);

    // Token QR dài và không theo dạng mã RET — đoán theo hình dạng, BE nhận
    // cả `RET-000482` | `ret-000482` | `482` ở tham số return_code.
    const looksLikeCode = /^(ret-)?\d+$/i.test(value);
    scanReturn(
      looksLikeCode ? { returnCode: value } : { token: value },
      {
        onSuccess: (result) => {
          if (result.return_request) setSelected(result.return_request);
          // blocked_reason là câu BE soạn để nhân viên đọc cho khách
          if (!result.actionable) setScanBlocked(result.blocked_reason || t('returnNotActionable'));
          setScanInput('');
        },
        onError: (err: unknown) => {
          setScanError(
            isApiError(err) && err.status === 404
              ? t('returnScanNotFound')
              : t('returnScanFailed')
          );
        },
      }
    );
  };

  const columns: Column<ReturnRequest>[] = [
    {
      key: 'return_code',
      header: t('returnCodeColumn'),
      width: '140px',
      render: (row) => (
        <div>
          <p className="text-sm font-bold text-slate-800">{row.return_code}</p>
          <p className="text-[10px] text-slate-400">{formatDateTime(row.created_at)}</p>
        </div>
      ),
    },
    {
      key: 'items',
      header: t('returnItemsColumn'),
      render: (row) => (
        <p className="text-sm text-slate-700">
          {t('returnItemsCount', { count: row.item_count ?? row.items?.length ?? 0 })}
        </p>
      ),
    },
    {
      key: 'refund_amount',
      header: t('returnRefundColumn'),
      width: '120px',
      render: (row) => (
        <p className="text-sm font-semibold text-slate-900">
          {/* Tiền từ BE là chuỗi decimal — ép sang number ở biên hiển thị.
              `currency` lấy từ CHÍNH yêu cầu trả hàng: hàng đợi có thể gồm
              nhiều cửa hàng khác thị trường, dùng tiền tệ của cửa hàng đang
              chọn là dán sai ký hiệu lên số của chi nhánh khác. */}
          {formatMoney(Number(row.refund_amount), { currency: row.currency })}
        </p>
      ),
    },
    {
      key: 'status',
      header: t('status'),
      width: '160px',
      render: (row) => (
        <span
          className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-bold ${
            STATUS_STYLES[row.status] ?? 'bg-slate-100 text-slate-700'
          }`}
        >
          {t(`returnStatus${row.status}`)}
        </span>
      ),
    },
    {
      key: 'action',
      header: '',
      width: '100px',
      render: (row) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setSelected(row);
          }}
          className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
        >
          {t('returnOpen')}
        </button>
      ),
    },
  ];

  return (
    <PageContainer>
      <PageHeader title={t('returnsTitle')} subtitle={t('returnsSubtitle')} />

      {/* Tra cứu: QR token hoặc mã RET khách đọc qua quầy */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
        <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
          {t('returnScanLabel')}
        </label>
        <div className="flex flex-wrap gap-2">
          <input
            value={scanInput}
            onChange={(e) => {
              setScanInput(e.target.value);
              if (scanBlocked || scanError) {
                setScanBlocked(null);
                setScanError(null);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleScan();
            }}
            placeholder={t('returnScanPlaceholder')}
            className="min-w-[240px] flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary-400"
          />
          <button
            onClick={handleScan}
            disabled={!scanInput.trim() || isScanning}
            className="rounded-lg bg-primary-600 px-5 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60"
          >
            {isScanning ? t('returnScanning') : t('returnScanSubmit')}
          </button>
        </div>
        <p className="mt-1.5 text-xs text-slate-400">{t('returnScanHint')}</p>
        {scanBlocked ? (
          <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {scanBlocked}
          </div>
        ) : null}
        {scanError ? (
          <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {scanError}
          </div>
        ) : null}
      </div>

      {error && (
        <div className="mt-4 rounded-lg bg-red-50 p-4 text-sm text-red-700">
          {error instanceof Error ? error.message : t('returnsLoadError')}
        </div>
      )}

      {/* Hàng đợi: mặc định việc còn tồn; tab riêng cho việc của người duyệt */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white px-4 py-3">
        <SelectDropdown
          options={[
            { value: 'queue', label: t('returnViewOpen') },
            { value: 'awaiting', label: t('returnViewAwaitingApproval') },
          ]}
          value={view}
          onChange={(val) => {
            setView(val as 'queue' | 'awaiting');
            setCurrentPage(1);
          }}
          className="w-full sm:w-[240px]"
        />
      </div>

      <div className="mt-4 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <DataTable
          columns={columns}
          data={rows}
          rowKey="id"
          isLoading={isLoading}
          onRowClick={(row) => setSelected(row)}
          showFilter={false}
          showHeaderMenu={false}
          emptyMessage={t('returnsEmpty')}
          className="border-0 shadow-none"
        />
        {!isLoading && totalItems > 0 && (
          <div className="border-t border-slate-100">
            <Pagination
              variant="numbered"
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={PAGE_SIZE}
              onPageChange={setCurrentPage}
              itemLabel="returns"
            />
          </div>
        )}
      </div>

      {selected && (
        <ReturnDetailModal
          request={selected}
          canDecideMoney={canDecideMoney}
          onClose={() => setSelected(null)}
          onUpdated={setSelected}
        />
      )}
    </PageContainer>
  );
}

// ============================================
// Modal xử lý một yêu cầu
// ============================================

function ReturnDetailModal({
  request,
  canDecideMoney,
  onClose,
  onUpdated,
}: {
  request: ReturnRequest;
  canDecideMoney: boolean;
  onClose: () => void;
  onUpdated: (next: ReturnRequest) => void;
}) {
  const { t } = useI18n();
  const { mutate: approve, isPending: isApproving } = useApproveReturn();
  const { mutate: reject, isPending: isRejecting } = useRejectReturn();
  const { mutate: receive, isPending: isReceiving } = useReceiveReturn();
  const { mutate: refund, isPending: isRefunding } = useRefundReturn();

  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);
  /** item_id -> số thực nhận (chuỗi người dùng gõ). Rỗng = nhận đủ. */
  const [receivedQty, setReceivedQty] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [blockingError, setBlockingError] = useState<string | null>(null);

  const busy = isApproving || isRejecting || isReceiving || isRefunding;
  const status = request.status;
  const canApproveOrReject = status === 'PENDING_APPROVAL' || status === 'CREATED';
  const canReceive = status === 'APPROVED';
  const canRefund = status === 'ITEMS_RECEIVED';

  const handleApiError = (err: unknown, fallbackKey: string) => {
    const reason =
      isApiError(err)
        ? ((err.data as { errors?: { reason?: string } } | undefined)?.errors?.reason ?? '')
        : '';
    // Có lần hoàn trước đang PENDING/FAILED — tiền chưa về. KHÔNG cho retry:
    // bảo người ta đi kiểm tra cổng thanh toán (BE 03/09).
    if (reason === 'refund_needs_reconciliation') {
      setBlockingError(t('returnRefundNeedsReconciliation'));
      return;
    }
    // Nhận hàng khi chưa duyệt
    if (reason === 'awaiting_approval') {
      setBlockingError(t('returnAwaitingApprovalBlock'));
      return;
    }
    setFormError(err instanceof Error ? err.message : t(fallbackKey));
  };

  const handleReceive = () => {
    setFormError(null);
    // Chỉ gửi dòng khách mang THIẾU; dòng bỏ trống = nhận đủ.
    const received = (request.items ?? [])
      .map((item) => {
        const raw = receivedQty[String(item.id ?? item.order_item_id)];
        if (raw === undefined || raw.trim() === '') return null;
        const parsed = Number.parseInt(raw, 10);
        if (!Number.isFinite(parsed) || parsed < 0) return null;
        if (parsed === item.quantity) return null;
        return { item_id: String(item.id ?? ''), quantity: String(parsed) };
      })
      .filter((x): x is { item_id: string; quantity: string } => x !== null && !!x.item_id);

    receive(
      { id: request.id as UUID, received: received.length > 0 ? received : undefined },
      {
        onSuccess: (updated) => onUpdated(updated),
        onError: (err) => handleApiError(err, 'returnReceiveFailed'),
      }
    );
  };

  const handleReject = () => {
    setFormError(null);
    if (!rejectReason.trim()) {
      setFormError(t('returnRejectReasonRequired'));
      return;
    }
    reject(
      { id: request.id as UUID, reason: rejectReason.trim() },
      {
        onSuccess: (updated) => onUpdated(updated),
        onError: (err) => handleApiError(err, 'returnRejectFailed'),
      }
    );
  };

  const renderItem = (item: ReturnRequestItem) => {
    const key = String(item.id ?? item.order_item_id);
    const receivedValue = receivedQty[key] ?? '';
    const hasMismatch =
      // So bằng Number: BE trả số lượng dạng chuỗi decimal (`"1.00"`), nên
      // `!==` trực tiếp cho `1 !== "1.00"` = true và dựng cờ lệch trên MỌI dòng
      // khớp nhau. Cùng cách màn khách đang làm.
      item.received_quantity != null &&
      Number(item.received_quantity) !== Number(item.quantity);

    return (
      <div key={key} className="rounded-xl border border-slate-200 p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-slate-800">{item.name}</p>
            <p className="text-xs text-slate-500">
              {t(`returnReason${item.reason}`)}
              {' · '}
              {/* Dòng hàng không mang currency riêng — lấy của yêu cầu chứa nó */}
              {formatMoney(Number(item.refund_amount), { currency: request.currency })}
            </p>
            {item.reason_note ? (
              <p className="mt-1 text-xs italic text-slate-600">“{item.reason_note}”</p>
            ) : null}
            {/* Khi lệch phải hiện CẢ HAI số — khách khai bao nhiêu, thực nhận
                bao nhiêu; tiền hoàn tính theo số thực nhận. */}
            <p className="mt-1 text-xs text-slate-500">
              {t('returnClaimedQty', { qty: String(item.quantity) })}
              {item.received_quantity != null ? (
                <>
                  {' · '}
                  <span className={hasMismatch ? 'font-bold text-amber-700' : 'text-slate-600'}>
                    {t('returnReceivedQty', { qty: String(item.received_quantity) })}
                  </span>
                </>
              ) : null}
            </p>
          </div>

          {canReceive ? (
            <div className="shrink-0">
              <label className="mb-1 block text-[10px] font-bold uppercase text-slate-400">
                {t('returnReceivedLabel')}
              </label>
              <input
                type="number"
                min={0}
                max={item.quantity}
                value={receivedValue}
                onChange={(e) =>
                  setReceivedQty((prev) => ({ ...prev, [key]: e.target.value }))
                }
                placeholder={String(item.quantity)}
                className="w-20 rounded-lg border border-slate-200 px-2 py-1.5 text-sm outline-none focus:border-primary-400"
              />
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 backdrop-blur-sm"
      onClick={busy ? undefined : onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-[600px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h3 className="text-lg font-bold text-slate-900">{request.return_code}</h3>
            <div className="mt-1 flex items-center gap-2">
              <span
                className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-bold ${
                  STATUS_STYLES[status] ?? 'bg-slate-100 text-slate-700'
                }`}
              >
                {t(`returnStatus${status}`)}
              </span>
              <span className="text-sm font-semibold text-slate-700">
                {formatMoney(Number(request.refund_amount), { currency: request.currency })}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={busy}
            className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 disabled:opacity-40"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto p-6">
          {request.rejection_reason ? (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {t('returnRejectionReason')}: {request.rejection_reason}
            </div>
          ) : null}

          {canReceive ? (
            <p className="rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-800">
              {t('returnReceiveHint')}
            </p>
          ) : null}

          {(request.items ?? []).map(renderItem)}

          {showReject && canDecideMoney ? (
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
                {t('returnRejectReasonLabel')} <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                placeholder={t('returnRejectReasonPlaceholder')}
                className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary-400"
              />
            </div>
          ) : null}

          {blockingError ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-800">
              {blockingError}
            </div>
          ) : null}
          {formError ? (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{formError}</div>
          ) : null}
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 px-6 py-4">
          <button
            onClick={onClose}
            disabled={busy}
            className="rounded-lg bg-slate-100 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200 disabled:opacity-50"
          >
            {t('close')}
          </button>

          {/* Việc ở quầy — OPERATOR trở lên */}
          {canReceive ? (
            <button
              onClick={handleReceive}
              disabled={busy || !!blockingError}
              className="rounded-lg bg-primary-600 px-5 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {isReceiving ? t('saving') : t('returnReceiveSubmit')}
            </button>
          ) : null}

          {/* Quyết định về tiền — chỉ STORE_ADMIN / AI_ADMIN */}
          {canDecideMoney && canApproveOrReject ? (
            showReject ? (
              <button
                onClick={handleReject}
                disabled={busy}
                className="rounded-lg bg-red-600 px-5 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {isRejecting ? t('saving') : t('returnConfirmReject')}
              </button>
            ) : (
              <>
                <button
                  onClick={() => setShowReject(true)}
                  disabled={busy}
                  className="rounded-lg border border-red-200 px-5 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                >
                  {t('returnReject')}
                </button>
                <button
                  onClick={() =>
                    approve(request.id as UUID, {
                      onSuccess: (updated) => onUpdated(updated),
                      onError: (err) => handleApiError(err, 'returnApproveFailed'),
                    })
                  }
                  disabled={busy}
                  className="rounded-lg bg-emerald-600 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  {isApproving ? t('saving') : t('returnApprove')}
                </button>
              </>
            )
          ) : null}

          {canDecideMoney && canRefund ? (
            <button
              onClick={() =>
                refund(request.id as UUID, {
                  onSuccess: (updated) => onUpdated(updated),
                  onError: (err) => handleApiError(err, 'returnRefundFailed'),
                })
              }
              disabled={busy || !!blockingError}
              className="rounded-lg bg-primary-600 px-5 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {isRefunding ? t('saving') : t('returnRefundSubmit')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
